/**
 * Base storage service implementing offline-first patterns
 *
 * Provides common functionality for all storage services including:
 * - Error handling for offline scenarios
 * - Background sync queue management
 * - Conflict resolution preparation
 * - Local data migration utilities
 */

import { logger } from '../utils/logger.js'
import type { Table } from 'dexie'
import { db } from '../database/schema.js'
import type { SyncEvent } from '../database/schema.js'
import type { ValidationResult } from './validation.js'
import { saveBaseSnapshot } from './base-snapshot.js'
import { isApplyingServerChanges } from './server-apply.js'
import { schedulePush } from './sync-push-scheduler.js'

const log = logger.create('storage')

/**
 * Storage operation result
 */
export interface StorageResult<T> {
  success: boolean
  data?: T | undefined
  error?: StorageError | undefined
  syncEvent?: SyncEvent | undefined
}

/**
 * Storage error with context for offline scenarios
 */
export interface StorageError {
  code: string
  message: string
  context?: 'offline' | 'conflict' | 'validation' | 'storage' | 'network' | undefined
  details?: unknown
  retryable: boolean
}

/**
 * Query options for storage operations
 */
export interface QueryOptions {
  limit?: number | undefined
  offset?: number | undefined
  orderBy?: string | undefined
  orderDirection?: 'asc' | 'desc' | undefined
  where?: Record<string, unknown> | undefined
  includeDeleted?: boolean | undefined
}

/**
 * Sync status enumeration
 */
export enum SyncStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  SYNCED = 'synced',
  FAILED = 'failed',
  CONFLICT = 'conflict',
}

/**
 * Base storage service class
 */
export abstract class BaseStorageService<T extends { id?: string }> {
  protected constructor(
    protected table: Table<T>,
    protected entityType: string
  ) {}

  /**
   * Create error with context
   */
  protected createError(
    code: string,
    message: string,
    context: StorageError['context'] = 'storage',
    details?: unknown,
    retryable = false
  ): StorageError {
    return {
      code,
      message,
      context,
      details,
      retryable,
    }
  }

  /**
   * Generate UUID for new entities
   */
  protected generateId(): string {
    return crypto.randomUUID()
  }

  /**
   * Handle validation errors
   */
  protected handleValidationError(validation: ValidationResult): StorageResult<never> {
    const errorMessages = validation.errors.map(e => `${e.field}: ${e.message}`).join(', ')
    return {
      success: false,
      error: this.createError(
        'VALIDATION_FAILED',
        `Validation failed: ${errorMessages}`,
        'validation',
        validation.errors
      ),
    }
  }

  /**
   * Handle storage errors with offline context
   */
  protected handleStorageError(error: unknown, operation: string): StorageResult<never> {
    if (error instanceof Error) {
      // Check for offline-related errors
      if (error.name === 'QuotaExceededError') {
        return {
          success: false,
          error: this.createError(
            'STORAGE_QUOTA_EXCEEDED',
            'Storage quota exceeded. Please free up space.',
            'offline',
            error,
            false
          ),
        }
      }

      if (error.name === 'DataError' || error.name === 'ConstraintError') {
        return {
          success: false,
          error: this.createError(
            'DATA_CONSTRAINT_ERROR',
            `Data constraint violation during ${operation}`,
            'storage',
            error,
            false
          ),
        }
      }

      // Network-related errors during sync
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return {
          success: false,
          error: this.createError(
            'NETWORK_ERROR',
            `Network error during ${operation}`,
            'network',
            error,
            true
          ),
        }
      }
    }

    // Generic storage error
    return {
      success: false,
      error: this.createError(
        'STORAGE_ERROR',
        `Storage operation failed: ${operation}`,
        'storage',
        error,
        true
      ),
    }
  }

  /**
   * Create sync event for operation
   */
  protected createSyncEvent(
    entityId: string,
    operation: 'create' | 'update' | 'delete',
    data?: Record<string, unknown>
  ): void {
    if (isApplyingServerChanges()) return
    const event: SyncEvent = {
      id: this.generateId(),
      entityType: this.entityType,
      entityId,
      operation,
      data: data ?? {},
      timestamp: new Date().toISOString(),
      conflictResolved: false,
    }
    db.syncEvents.add(event).catch(err => {
      log.error('Failed to persist sync event', err)
    })
    schedulePush()
  }

  /**
   * Execute operation with error handling
   */
  protected async executeOperation<R>(
    operation: () => Promise<R>,
    operationName: string
  ): Promise<StorageResult<R>> {
    try {
      const result = await operation()
      return { success: true, data: result }
    } catch (error) {
      return this.handleStorageError(error, operationName)
    }
  }

  /**
   * Get entity by ID with error handling
   */
  async getById(id: string): Promise<StorageResult<T | undefined>> {
    return this.executeOperation(() => this.table.get(id), `get ${this.entityType} by ID`)
  }

  /**
   * Get all entities with optional filtering
   */
  async getAll(options: QueryOptions = {}): Promise<StorageResult<T[]>> {
    return this.executeOperation(async () => {
      let query = this.table.toCollection()

      // Apply where filters
      if (options.where) {
        for (const [field, value] of Object.entries(options.where)) {
          query = query.filter(item => (item as Record<string, unknown>)[field] === value)
        }
      }

      // Apply ordering
      if (options.orderBy) {
        let sorted = await query.sortBy(options.orderBy)
        if (options.orderDirection === 'desc') {
          sorted = sorted.reverse()
        }

        // Apply pagination
        const start = options.offset ?? 0
        const end = options.limit ? start + options.limit : undefined
        return sorted.slice(start, end)
      }

      // Apply pagination
      if (options.offset) {
        query = query.offset(options.offset)
      }
      if (options.limit) {
        query = query.limit(options.limit)
      }

      return await query.toArray()
    }, `get all ${this.entityType}`)
  }

  /**
   * Count entities with optional filtering
   */
  async count(where?: Record<string, unknown>): Promise<StorageResult<number>> {
    return this.executeOperation(async () => {
      let query = this.table.toCollection()

      if (where) {
        for (const [field, value] of Object.entries(where)) {
          query = query.filter(item => (item as Record<string, unknown>)[field] === value)
        }
      }

      return await query.count()
    }, `count ${this.entityType}`)
  }

  /**
   * Check if entity exists
   */
  async exists(id: string): Promise<StorageResult<boolean>> {
    return this.executeOperation(async () => {
      const item = await this.table.get(id)
      return item !== undefined
    }, `check ${this.entityType} exists`)
  }

  /**
   * Delete entity by ID
   */
  async deleteById(id: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      await this.table.delete(id)
      return undefined
    }, `delete ${this.entityType}`)
  }

  /**
   * Delete multiple entities by IDs
   */
  async deleteByIds(ids: string[]): Promise<StorageResult<void>> {
    return this.executeOperation(() => this.table.bulkDelete(ids), `bulk delete ${this.entityType}`)
  }

  /**
   * Clear all entities (use with caution)
   */
  async clear(): Promise<StorageResult<void>> {
    return this.executeOperation(() => this.table.clear(), `clear all ${this.entityType}`)
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<StorageResult<{ count: number; size?: number | undefined }>> {
    return this.executeOperation(async () => {
      const count = await this.table.count()

      // Try to estimate storage size if available
      let size: number | undefined
      try {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const estimate = await navigator.storage.estimate()
          size = estimate.usage
        }
      } catch (e) {
        // Storage estimation not available
      }

      return { count, size }
    }, `get ${this.entityType} statistics`)
  }
}

/**
 * Offline-first patterns mixin
 */
export abstract class OfflineFirstStorageService<
  T extends { id?: string; createdAt?: string; updatedAt?: string },
> extends BaseStorageService<T> {
  /**
   * Create entity with offline-first patterns
   */
  async create(data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<StorageResult<T>> {
    return this.executeOperation(async () => {
      const id = this.generateId()
      const now = new Date().toISOString()

      const entity = {
        ...data,
        id,
        version: 0,
        createdAt: now,
        updatedAt: now,
      } as unknown as T

      await this.table.add(entity)

      // Create sync event for background sync
      this.createSyncEvent(id, 'create', entity)

      return entity
    }, `create ${this.entityType}`)
  }

  /**
   * Update entity with offline-first patterns
   */
  async update(
    id: string,
    updates: Partial<Omit<T, 'id' | 'createdAt'>>
  ): Promise<StorageResult<T>> {
    return this.executeOperation(async () => {
      const existing = await this.table.get(id)
      if (!existing) {
        throw new Error(`${this.entityType} not found`)
      }

      await saveBaseSnapshot(this.entityType, id, existing as unknown as Record<string, unknown>)

      const updatedEntity = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedEntity)

      this.createSyncEvent(id, 'update', updates)

      return updatedEntity
    }, `update ${this.entityType}`)
  }

  /**
   * Upsert entity (create or update)
   */
  async upsert(data: T): Promise<StorageResult<T>> {
    if (data.id) {
      const existsResult = await this.exists(data.id)
      if (existsResult.success && existsResult.data) {
        return this.update(data.id, data as Partial<T>)
      }
    }

    return this.create(data as Omit<T, 'id' | 'createdAt' | 'updatedAt'>)
  }

  /**
   * Soft delete entity (mark as deleted but keep for sync)
   */
  async softDelete(id: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const existing = await this.table.get(id)
      if (!existing) {
        throw new Error(`${this.entityType} not found`)
      }

      // Mark as deleted instead of removing
      const deletedEntity = {
        ...existing,
        updatedAt: new Date().toISOString(),
        // Add deleted flag if the type supports it
        ...('deleted' in existing ? { deleted: true } : {}),
      }

      await this.table.put(deletedEntity)

      // Create sync event for deletion
      this.createSyncEvent(id, 'delete')

      return undefined
    }, `soft delete ${this.entityType}`)
  }
}

/**
 * Utility functions for offline storage
 */
export class StorageUtils {
  /**
   * Check if device is online
   */
  static isOnline(): boolean {
    return navigator.onLine
  }

  /**
   * Estimate available storage space
   */
  static async getStorageEstimate(): Promise<{
    quota?: number | undefined
    usage?: number | undefined
    available?: number | undefined
  }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate()
        return {
          quota: estimate.quota,
          usage: estimate.usage,
          available: estimate.quota && estimate.usage ? estimate.quota - estimate.usage : undefined,
        }
      } catch (e) {
        log.warn('Storage estimation failed:', e)
      }
    }
    return {}
  }

  /**
   * Check if storage quota is running low
   */
  static async isStorageLow(): Promise<boolean> {
    const estimate = await this.getStorageEstimate()
    if (estimate.quota && estimate.usage) {
      const usagePercentage = estimate.usage / estimate.quota
      return usagePercentage > 0.9 // 90% usage threshold
    }
    return false
  }

  /**
   * Format storage size for display
   */
  static formatStorageSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`
  }
}
